/**
 * Tenant da sessão no painel (#319).
 *
 * O slug guardado no localStorage (`sr_tenant_slug`) é só um atalho: a fonte
 * de verdade é o tenant do token, que a API devolve em GET /auth/session sem
 * depender do X-Tenant-Slug. Um slug guardado que não é o do tenant da sessão
 * (tenant apagado, conta recriada, outro restaurante) é descartado.
 */
import { auth, refreshSession } from './api.js'
import type { SessionInfo, SessionMembership } from './api.js'
import {
  clearStoredTenantSlug,
  clearToken,
  getToken,
  getTokenPayload,
  readStoredTenantSlugOrNull,
  setStoredTenantSlug,
  setToken,
} from './auth.js'

/** Seletor de restaurante (passo "Escolher acesso" do /login) com a sessão atual. */
export const PICKER_PATH = '/login?escolher=1'

export function isPickerRequest(search: string): boolean {
  return new URLSearchParams(search).has('escolher')
}

/** Tela inicial por role após login. */
export function homePathFor(role: string | undefined, slug: string | null): string {
  if (role === 'platform_owner') return '/'
  if (!slug || slug === 'platform') return '/'
  if (role === 'kitchen') return `/r/${slug}/kitchen`
  // Gerente (#284) entra pela operação do salão
  if (role === 'waiter' || role === 'manager') return `/r/${slug}/waiter`
  return `/r/${slug}/dashboard`
}

export type HomeDecision = {
  /** Para onde ir; null = sessão sem restaurante válido → /login. */
  path: string | null
  /** O que fazer com o slug guardado: string = gravar, null = apagar, undefined = manter. */
  stored: string | null | undefined
}

/**
 * Decisão pura do destino "início" (rota desconhecida, /login com sessão).
 * `session` null = /auth/session falhou (rede): sem palpite com slug guardado.
 */
export function decideHome(
  role: string | undefined,
  storedSlug: string | null,
  session: SessionInfo | null,
): HomeDecision {
  if (!role) return { path: null, stored: undefined }
  if (role === 'platform_owner') return { path: '/', stored: undefined }
  if (!session) return { path: '/', stored: undefined }
  const current = session.current
  // Token sem vínculo de staff no próprio tenant: token pós-OTP da plataforma
  // (cliente) com restaurantes → seletor (recarregar o /login no seletor não
  // pede OTP de novo); tenant apagado/inativo ou sem vínculos → /login
  if (!current) {
    const picker = session.memberships.length > 0 && !!session.tenant_slug
    return { path: picker ? PICKER_PATH : null, stored: null }
  }
  const slug = current.tenant_slug
  if (storedSlug === slug) return { path: homePathFor(role, slug), stored: slug }
  // Slug guardado obsoleto (ou ausente): quem tem vários restaurantes escolhe
  if (session.memberships.length > 1) return { path: PICKER_PATH, stored: null }
  return { path: homePathFor(role, slug), stored: slug }
}

function applyStored(stored: string | null | undefined): void {
  if (stored === null) clearStoredTenantSlug()
  else if (stored !== undefined) setStoredTenantSlug(stored)
}

async function loadSession(fetchSession: () => Promise<SessionInfo>): Promise<SessionInfo | null> {
  try {
    return await fetchSession()
  } catch {
    return null
  }
}

/** Resolve (e grava/apaga o slug guardado) o destino inicial da sessão atual. */
export async function resolveHomePath(
  fetchSession: () => Promise<SessionInfo> = auth.session,
): Promise<string | null> {
  const role = getTokenPayload()?.role
  const needsSession = !!role && role !== 'platform_owner'
  const session = needsSession ? await loadSession(fetchSession) : null
  const decision = decideHome(role, readStoredTenantSlugOrNull(), session)
  applyStored(decision.stored)
  return decision.path
}

/** Resultado do guard de tenant em /r/:slug: renderizar, ou ir para outro lugar. */
export type TenantGate = { kind: 'ok' } | { kind: 'redirect'; to: string }

export type TenantGateDeps = {
  fetchSession: () => Promise<SessionInfo>
  /** POST /auth/refresh pelo cookie (que pode ser de outro tenant). */
  refresh: () => Promise<boolean>
  /** POST /auth/select (troca de tenant com a sessão atual). */
  select: (tenantId: string, role: string, contextSlug: string) => Promise<{ access_token: string }>
}

const defaultGateDeps: TenantGateDeps = {
  fetchSession: () => auth.session(),
  refresh: () => refreshSession(),
  select: (tenantId, role, contextSlug) => auth.select(tenantId, role, contextSlug),
}

/** Ordem de preferência da role ao entrar num restaurante pela URL. */
const ROLE_PREFERENCE = ['restaurant_owner', 'manager', 'waiter', 'kitchen']
export function preferredRole(roles: readonly string[]): string | undefined {
  return ROLE_PREFERENCE.find((r) => roles.includes(r)) ?? roles[0]
}

/** tenant_id → slug já confirmado pela sessão (evita o spinner a cada página). */
const confirmedTenants = new Map<string, string>()
/** Último refresh forçado (token + slug da URL): nunca repete para o mesmo par. */
let lastForcedRefresh: string | null = null
let inflightGate: { key: string; promise: Promise<TenantGate> } | null = null

/** Token + slug liberados sem confirmação (/auth/session fora: sem palpite). */
let releasedFor: string | null = null

// Quem depende do guard fora do router (dicionário do i18n) é avisado quando
// um tenant fica confirmado
const gateListeners = new Set<() => void>()
export function onTenantGateChange(fn: () => void): () => void {
  gateListeners.add(fn)
  return () => gateListeners.delete(fn)
}
function notifyGate(): void {
  for (const fn of gateListeners) fn()
}

/** Só para testes: esquece os tenants confirmados e o último refresh forçado. */
export function resetTenantGate(): void {
  confirmedTenants.clear()
  lastForcedRefresh = null
  inflightGate = null
  releasedFor = null
}

/**
 * True se /r/<pathSlug> pode renderizar já: sem token, platform_owner (vê
 * qualquer restaurante) ou token cujo tenant já foi confirmado como este slug.
 */
export function tenantGateSatisfied(pathSlug: string): boolean {
  const payload = getTokenPayload()
  if (!payload?.role || payload.role === 'platform_owner') return true
  if (releasedFor === `${getToken() ?? ''}|${pathSlug}`) return true
  return confirmedTenants.get(payload.tenant_id) === pathSlug
}

/** Slug do path /r/:slug da página atual (null fora de /r/). */
function pathTenantSlug(): string | null {
  if (typeof window === 'undefined') return null
  return window.location.pathname.match(/^\/r\/([^/]+)/)?.[1] ?? null
}

/**
 * Contexto do dicionário do painel (#319): 'login' sem token; 'wait' em
 * /r/:slug enquanto o token não é deste tenant (o pedido daria 403 e ficaria
 * em cache); senão o tenant do token (troca de tenant = dicionário novo).
 */
export function dictionaryContext(): string {
  const payload = getTokenPayload()
  if (!payload) return 'login'
  const slug = pathTenantSlug()
  if (slug && !tenantGateSatisfied(slug)) return 'wait'
  return `tenant:${payload.tenant_id ?? ''}`
}

function confirm(pathSlug: string): TenantGate {
  const tenantId = getTokenPayload()?.tenant_id
  if (tenantId) confirmedTenants.set(tenantId, pathSlug)
  setStoredTenantSlug(pathSlug)
  notifyGate()
  return { kind: 'ok' }
}

/**
 * Guard de tenant em /r/:slug (#319; inclui o handoff do /contratar, #312,
 * que entra direto em /r/<novo>/dashboard com o cookie de refresh, sem /login).
 *
 * O painel só renderiza (e só dispara /admin/*) com um token do tenant da URL:
 * 1. tenant do token = slug da URL → grava o slug e segue;
 * 2. senão, UM refresh forçado (o cookie pode ser de outro tenant: /contratar
 *    com o painel aberto noutro restaurante, QA 3b) e nova consulta;
 * 3. ainda diferente e o usuário tem vínculo no slug da URL → troca de tenant
 *    pelo POST /auth/select (mesmo mecanismo do seletor);
 * 4. sem acesso → restaurante da sessão, seletor ou /login (nunca /r/<slug>).
 * No máximo 1 refresh forçado por navegação (e nunca 2× para o mesmo token e
 * slug): o destino do passo 4 é sempre o tenant do token, que passa no passo 1.
 */
export function ensureTenantSession(
  pathSlug: string,
  deps: TenantGateDeps = defaultGateDeps,
): Promise<TenantGate> {
  // Chamadas simultâneas para o mesmo token e slug (StrictMode, remount)
  // partilham a mesma resolução: um só refresh forçado
  const key = `${getToken() ?? ''}|${pathSlug}`
  if (inflightGate?.key === key) return inflightGate.promise
  const promise = resolveTenantGate(pathSlug, deps).finally(() => {
    if (inflightGate?.promise === promise) inflightGate = null
  })
  inflightGate = { key, promise }
  return promise
}

async function resolveTenantGate(pathSlug: string, deps: TenantGateDeps): Promise<TenantGate> {
  const role = getTokenPayload()?.role
  if (!role || role === 'platform_owner') return { kind: 'ok' }
  let session = await loadSession(deps.fetchSession)
  // /auth/session fora (rede): sem palpite, segue como antes
  if (!session) {
    releasedFor = `${getToken() ?? ''}|${pathSlug}`
    notifyGate()
    return { kind: 'ok' }
  }
  if (session.current?.tenant_slug === pathSlug) return confirm(pathSlug)

  const forcedKey = `${getToken() ?? ''}|${pathSlug}`
  if (lastForcedRefresh !== forcedKey) {
    lastForcedRefresh = forcedKey
    let refreshed = false
    try {
      refreshed = await deps.refresh()
    } catch {
      refreshed = false
    }
    if (refreshed && getTokenPayload()) {
      const after = await loadSession(deps.fetchSession)
      if (after) session = after
      if (session.current?.tenant_slug === pathSlug) return confirm(pathSlug)
    }
    // refresh falhou e levou o token (sessão acabou de vez) → /login
    if (!getTokenPayload()) return { kind: 'redirect', to: '/login' }
  }

  const target: SessionMembership | undefined = session.memberships.find(
    (m) => m.tenant_slug === pathSlug,
  )
  const contextSlug = session.current?.tenant_slug ?? session.tenant_slug ?? null
  const targetRole = target ? preferredRole(target.roles) : undefined
  if (target && targetRole && contextSlug) {
    try {
      const result = await deps.select(target.tenant_id, targetRole, contextSlug)
      setToken(result.access_token)
      return confirm(pathSlug)
    } catch {
      /* sem troca: cai no destino da sessão */
    }
  }

  const stored = readStoredTenantSlugOrNull()
  if (stored && !session.memberships.some((m) => m.tenant_slug === stored)) {
    clearStoredTenantSlug()
  }
  // (aqui current nunca é o slug da URL: esse caso já saiu no passo 1)
  const current = session.current
  if (current) {
    return { kind: 'redirect', to: homePathFor(getTokenPayload()?.role, current.tenant_slug) }
  }
  if (session.memberships.length > 0 && contextSlug) return { kind: 'redirect', to: PICKER_PATH }
  return { kind: 'redirect', to: '/login' }
}

/** Logout do painel: encerra na API e apaga token e slug guardado (#319). */
export async function logoutSession(
  apiLogout: () => Promise<unknown> = auth.logout,
): Promise<void> {
  try {
    await apiLogout()
  } catch {
    /* ignora: a sessão local é encerrada de qualquer forma */
  }
  const { clearAllImpersonationState } = await import('./impersonation.js')
  clearAllImpersonationState()
  clearStoredTenantSlug()
  clearToken()
}
